import { useRef, useEffect } from 'react';
import { isSameDay } from '../../../../lib/date';
import { useTodayKey } from '../../../../hooks/useTodayKey';

export interface DateStripProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  datesWithItems?: Set<string> | Map<string, string[]>;
  month: Date;
  mode?: 'month' | 'week';
  /**
   * User's profile timezone. "Today"/"past" are resolved in this timezone
   * (matching the schedule feed's getTodayKey bucketing) so a device clock in
   * a different timezone can't mis-classify a feed-past day as today/enabled.
   */
  userTimezone?: string | null;
}

const DAY_NAMES = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function getDaysInMonth(month: Date): Date[] {
  const year = month.getFullYear();
  const m = month.getMonth();
  const days: Date[] = [];
  const count = new Date(year, m + 1, 0).getDate();
  for (let d = 1; d <= count; d++) {
    days.push(new Date(year, m, d));
  }
  return days;
}

function getWeekDays(selectedDate: Date): Date[] {
  const dayOfWeek = selectedDate.getDay();
  const weekStart = new Date(selectedDate);
  weekStart.setDate(selectedDate.getDate() - dayOfWeek);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    days.push(d);
  }
  return days;
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whether the day carries anything at all. The Map form is still accepted
 * because callers hand one over, but its per-item colours no longer matter:
 * the cell says "something is here", not "how many" or "whose" — the dots
 * that tried to say more were removed in YAR-420.
 */
function hasItemsOn(
  datesWithItems: Set<string> | Map<string, string[]> | undefined,
  dateKey: string,
): boolean {
  if (!datesWithItems) return false;
  return datesWithItems.has(dateKey);
}

export function DateStrip({
  selectedDate,
  onDateSelect,
  datesWithItems,
  month,
  mode = 'month',
  userTimezone,
}: DateStripProps) {
  const days =
    mode === 'week' ? getWeekDays(selectedDate) : getDaysInMonth(month);
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Resolve "today" in the user's profile timezone (the same source the
  // schedule feed uses) rather than the device clock, so the strip and the
  // feed always agree on which days are past. useTodayKey re-renders at
  // the timezone's midnight, so a strip left mounted across the day
  // boundary can't keep yesterday's cell enabled.
  const todayKey = useTodayKey(userTimezone);

  useEffect(() => {
    if (selectedRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const el = selectedRef.current;
      const offset =
        el.offsetLeft - container.offsetWidth / 2 + el.offsetWidth / 2;
      container.scrollTo({ left: offset, behavior: 'smooth' });
    }
  }, [selectedDate]);

  return (
    <div
      ref={scrollRef}
      className={[
        'flex flex-col py-2 px-1 scrollbar-none',
        mode === 'week' ? '' : 'overflow-x-auto',
      ].join(' ')}
    >
      {/* Day letter headers row */}
      <div
        className={['flex', mode === 'week' ? 'justify-between' : 'gap-1'].join(
          ' ',
        )}
      >
        {days.map((day) => (
          <div
            key={`h-${day.getDate()}`}
            className="flex items-center justify-center min-w-[3rem] px-1"
          >
            <span className="text-xs font-medium text-muted-foreground">
              {DAY_NAMES[day.getDay()]}
            </span>
          </div>
        ))}
      </div>

      {/* Date number buttons row */}
      <div
        className={[
          'flex mt-1',
          mode === 'week' ? 'justify-between' : 'gap-1',
        ].join(' ')}
      >
        {days.map((day) => {
          const dayKey = toDateKey(day);
          const isSelected = isSameDay(day, selectedDate);
          const isToday = dayKey === todayKey;
          const isPast = dayKey < todayKey;
          const hasItems = hasItemsOn(datesWithItems, dayKey);

          return (
            <button
              key={day.getDate()}
              ref={isSelected ? selectedRef : undefined}
              type="button"
              onClick={isPast ? undefined : () => onDateSelect(day)}
              className={[
                'flex flex-col items-center justify-center gap-0.5 min-w-[3rem] py-1.5 px-1 shrink-0',
                'transition-colors duration-150',
                isPast
                  ? 'text-disabled-text cursor-default'
                  : isSelected
                    ? 'cursor-pointer'
                    : 'text-foreground hover:bg-muted rounded-full cursor-pointer',
              ].join(' ')}
            >
              <span
                className={[
                  'flex items-center justify-center w-9 h-9 rounded-full border text-sm font-bold',
                  // Four composable cell states (YAR-132 ACs 14-18). Dots are
                  // gone: they cost a second row of vertical space to say one
                  // bit, and could not show a fill and a ring at once. The
                  // cell itself now carries the signal.
                  //
                  // A past day carries no fill, and the reason is what the
                  // fill MEANS: "there is something here for you to act on".
                  // A day that has gone has nothing left to act on, so the
                  // fill would be an invitation to a day that cannot be
                  // tapped.
                  //
                  // It is NOT that a past day may carry no marker at all.
                  // This comment used to say "past days get nothing at all"
                  // and cite YAR-132 AC #9, which says neither: AC #9 is
                  // "past days are visually disabled, tapping does nothing",
                  // and its own implementation (48a7cf7b) kept the item dots
                  // on past days and suppressed only the SELECTED badge. The
                  // broader wording arrived with the dots-to-fills change in
                  // PR #374, whose body never mentions past days.
                  //
                  // That mis-citation cost real time: YAR-418 (a multi-day
                  // run is invisible on the days it covers) and YAR-385 (a
                  // past day should not show a dot for a completed item) were
                  // read as contradicting each other and both stalled waiting
                  // on a product decision. They do not contradict — a run
                  // that is still going and an item in History are disjoint
                  // by construction (`getOngoingEvents` takes
                  // `end_date >= todayStart`, `getHistoryEvents` takes `<`).
                  // Coverage by a still-running span is carried by the span
                  // bar above the feed, which deliberately does NOT clip at
                  // today (`ongoing.ts` clamps `startCol` to 0), so a past
                  // day inside a running span is already communicated. The
                  // cell says "act here"; the bar says "a period runs
                  // through here". Two channels, on purpose.
                  //
                  // The premise this comment used to give is also gone: the
                  // feed has been floored at today since PR #249, past cells
                  // have no handler, and the `?date=` seed is clamped, so a
                  // past `selectedDate` can no longer occur at all.
                  //
                  // The viewed day is solid `surface/primary/default` whether
                  // or not it is today (Figma "Default": today, viewed, is
                  // the same #264133 cell). It used to be painted
                  // `foreground` (#2f2f2f) when the two coincided.
                  isPast
                    ? 'border-transparent'
                    : isSelected
                      ? 'bg-primary border-transparent text-primary-foreground'
                      : [
                          // Fill and ring are independent, so "today, and it
                          // has something on it" renders as a ring drawn
                          // over the fill rather than one state winning.
                          //
                          // One border-colour utility per badge. The ring used
                          // to be appended AFTER `border-transparent` / the
                          // fill's light border, trusting list order to win;
                          // Tailwind resolves by stylesheet order instead, and
                          // there `border-transparent` comes last, so today
                          // showed no ring at all once the feed scrolled off
                          // it (YAR-420 re-open, round 2).
                          hasItems ? 'bg-primary-light' : '',
                          // Today's ring is Figma `border/focus` #1f372d (`primary-hover`),
                          // one shade darker than the viewed day's solid #264133 fill.
                          isToday
                            ? 'border-primary-hover'
                            : hasItems
                              ? 'border-primary-light-border'
                              : 'border-transparent',
                        ]
                          .filter(Boolean)
                          .join(' '),
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {day.getDate()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
